import SectionLabel from "./SectionLabel";
import SmartLink from "./SmartLink";
import Reveal from "./Reveal";
import Img from "./Img";
import { destinations } from "../data/destinations";

/* Experience cards — themed on marketing's "Experiences" graphic (Nov 2026), same palette as
   the Registration Week day cards. Counts come from the destinations data, so they stay in
   step with the trip list. */
const countOf = (type: string) => destinations.filter((d) => d.type === type).length;

/** Shared with the Availability page's "Choose your experience" cards. `photo` (optional) is
 *  the top-half picture there; types without one show their color and name instead. */
export const experiences: {
  name: string;
  desc: string;
  count: number;
  bg: string;
  accent: string;
  text: string;
  wash: string;
  featured?: boolean;
  photo?: string;
}[] = [
  {
    name: "Preserve",
    desc: "Serve locals by protecting the earth.",
    count: countOf("Preserve"),
    bg: "bg-[#2e3c18]",
    accent: "text-[#cccb95]",
    text: "text-[#efe8de]",
    wash: "bg-[#2e3c18]/85",
    photo: "/images/experiences/preserve-photo.jpg", // Builders and locals with planted garden towers (marketing, Nov 2026)
  },
  {
    name: "Refuge",
    desc: "Serve in refugee camps around the world.",
    count: countOf("Refuge"),
    bg: "bg-[#8a6e2f]", // graphic's #a88b42 darkened for WCAG AA contrast with light text
    accent: "text-[#f3ebbb]",
    text: "text-white",
    wash: "bg-[#735b25]/85",
    photo: "/images/experiences/refuge-photo.jpg", // Malawi trip: Builders delivering school desks
  },
  {
    name: "Build",
    desc: "Serve locals by building sustainable infrastructure.",
    count: countOf("Build"),
    bg: "bg-[#b94737]",
    accent: "text-[#e9bab4]",
    text: "text-white",
    wash: "bg-[#8f3426]/85",
    featured: true,
    photo: "/images/experiences/build-crew.jpg", // Builders in hard hats and vests digging gravel (marketing, Nov 2026)
  },
  {
    name: "Teach",
    desc: "Serve locals by sharing your English language skills.",
    count: countOf("Teach"),
    bg: "bg-[#193f49]",
    accent: "text-[#a9c0be]",
    text: "text-[#efe8de]",
    wash: "bg-[#193f49]/85",
    photo: "/images/experiences/teach-photo.jpg", // a Builder making heart hands with students in a classroom (marketing, Nov 2026)
  },
  {
    name: "Love",
    desc: "Serve and love differently abled local communities.",
    count: countOf("Love"),
    bg: "bg-[#5f6db5]",
    accent: "text-[#d3d6f0]",
    text: "text-white",
    wash: "bg-[#4d5a9c]/85",
    photo: "/images/experiences/love-photo.jpg", // a Builder and a local friend, thumbs up (marketing, Nov 2026)
  },
];

const ExperiencesSection = () => (
  <section id="experiences" className="border-y border-hxp-sand-dark bg-[#efe8de] py-16 md:py-24">
    <Reveal>
      <div className="mx-auto max-w-[1200px] px-5 md:px-6">
        <SectionLabel>Our Experiences</SectionLabel>
        <h2 className="mt-6 max-w-3xl text-4xl md:text-5xl">
          Choose the experience that's <span className="text-primary">right for you</span>
        </h2>

        <div className="mt-8 grid text-center grid-cols-2 gap-3 md:mt-12 md:grid-cols-[1fr_1fr_2.3fr_1fr_1fr] md:gap-4">
          {experiences.map((exp) => (
            <SmartLink
              key={exp.name}
              href="/availability/"
              className={`group relative flex flex-col items-center overflow-hidden rounded-3xl px-3 pb-6 pt-6 transition-transform duration-300 hover:-translate-y-1 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-4 focus-visible:outline-[#383632] motion-reduce:transition-none motion-reduce:hover:translate-y-0 md:pb-8 ${exp.bg} ${
                exp.featured ? "col-span-2 md:col-span-1" : ""
              }`}
            >
              {/* Faint photo behind the card (marketing, Nov 2026); the wash keeps AA contrast */}
              {exp.photo && (
                <>
                  <Img src={exp.photo} alt="" sizes="(min-width: 768px) 450px, 100vw" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  <div className={`absolute inset-0 ${exp.wash}`} />
                </>
              )}
              {/* 2rem on desktop so the longest name (PRESERVE) keeps side margin in its narrow card */}
              <h3 className={`relative text-2xl md:text-[1.75rem] xl:text-[2rem] ${exp.accent}`}>{exp.name}</h3>
              <p className={`relative mt-2 max-w-[22rem] text-sm leading-snug md:text-base ${exp.text}`}>{exp.desc}</p>
              <p
                className={`font-heading relative mt-auto pt-4 leading-none ${exp.accent} ${
                  exp.featured ? "text-8xl md:text-[11rem]" : "text-7xl md:text-[9rem]"
                }`}
              >
                {exp.count}
                {/* Same type and color as the description line above */}
                <span className={`font-body mt-2 block text-sm font-normal leading-snug md:text-base ${exp.text}`}>
                  {" "}
                  {exp.count === 1 ? "Location" : "Locations"} in 2027
                </span>
              </p>
            </SmartLink>
          ))}
        </div>
      </div>
    </Reveal>
  </section>
);

export default ExperiencesSection;
